<script lang="ts">
    import type {LoanRequestOption, StandardOrderability} from 'src/typings';
    import {getInjector, getLocalization, getSanitize} from 'src/core';
    import {LoanRequestFormService} from '../loan-request-form.service';
    import KpGenericPanel from 'shared/ui-widgets/panel/KpGenericPanel.svelte';
    import KpChipTag from 'shared/ui-widgets/chip/KpChipTag.svelte';
    import KpPluralizeText from 'shared/components/kp-pluralize-text/KpPluralizeText.svelte';
    import KpUniversalForm from 'shared/value-editors/kp-universal-form/KpUniversalForm.svelte';
    import type {Field} from 'node_modules/svelte-forms/types';
    import type {FormGroup} from 'shared/value-editors/internal/forms/form-group';
    import {exists, isNullOrUndefined} from 'shared/utils/custom-utils';
    import LoginFirstButton from './shared/LoginFirstButton.svelte';
    import SendLoanRequestButton from './shared/SendLoanRequestButton.svelte';
    import DesiredBuilding from './shared/DesiredBuilding.svelte';
    import BuildingsList from './shared/BuildingsList.svelte';
    import LoanOptionPanelBody from './shared/LoanOptionPanelBody.svelte';
    import FullWidthBox from './shared/FullWidthBox.svelte';
    import type {Observable} from 'rxjs';

    export let option: LoanRequestOption<StandardOrderability>;

    const localize = getLocalization();
    const sanitize = getSanitize();
    const service = getInjector().getByClass(LoanRequestFormService);
    const additionalInfoText = localize(/* @kp-localization loan.standardOrderability.additionalModalInfo */ 'loan.standardOrderability.additionalModalInfo');

    $: showLoginButtonFirst = service.requiresLogin(option);

    let submitted = false;
    let getFormController: () => FormGroup<any>;
    let formState$: Observable<Field<any>>;

    $: {
        if (exists(getFormController)) {
            formState$ = getFormController().getFieldState$();
        }
    }

    function isInvalidRequest(request: typeof option.formObject): boolean {
        return isNullOrUndefined(request) || request.desiredBuilding === null;
    }
</script>

<KpGenericPanel>
    <svelte:fragment slot="heading">
        <strong>{localize(/* @kp-localization loan.Objednavka */ 'loan.Objednavka')}</strong>
        <KpChipTag chipSize="sm" chipStyle="info">{localize(/* @kp-localization loan.Absencni */ 'loan.Absencni')}</KpChipTag>
    </svelte:fragment>

    <LoanOptionPanelBody>
        <div>
            {#if option.requestItems.length === 1}
                <KpPluralizeText count={option.capability.buildings.length}
                                 oneCountText={localize(/* @kp-localization loan.DokumentJeKObjednaniNaBudove */ 'loan.DokumentJeKObjednaniNaBudove')}
                                 moreText={localize(/* @kp-localization loan.DokumentJeKObjednaniNaBudovach */ 'loan.DokumentJeKObjednaniNaBudovach')}/>
            {:else if option.requestItems.length > 1}
                <KpPluralizeText count={option.capability.buildings.length}
                                 oneCountText={localize(/* @kp-localization loan.DokumentyJsouKObjednaniNaBudove */ 'loan.DokumentyJsouKObjednaniNaBudove')}
                                 moreText={localize(/* @kp-localization loan.DokumentyJsouKObjednaniNaBudovach */ 'loan.DokumentyJsouKObjednaniNaBudovach')}/>
            {/if}
        </div>

        {#if additionalInfoText.length > 0}
            <div class="loan-additional-info">
                {@html sanitize(additionalInfoText)}
            </div>
        {/if}

        {#if showLoginButtonFirst}
            <BuildingsList {option}/>
            <LoginFirstButton/>
        {:else}
            <DesiredBuilding capability={option.capability} building={option.formObject.desiredBuilding}/>

            <FullWidthBox>
                <KpUniversalForm formSettings={option.form}
                                 model={option.formObject}
                                 forceShowErrors={submitted}
                                 bind:getFormController={getFormController}
                                 on:submit={() => submitted = true}/>
            </FullWidthBox>

            <SendLoanRequestButton {option}
                                   apiPath="/loan-items/request/standardOrderRequest"
                                   isDisabled={isInvalidRequest(option.formObject) || $formState$?.invalid}
                                   dataQa="standard-order-button">
                {localize(/* @kp-localization loan.Objednat */ 'loan.Objednat')}
            </SendLoanRequestButton>
        {/if}
    </LoanOptionPanelBody>
</KpGenericPanel>